import { UserRole } from '@textile-pos/types';
import {
  Activity,
  Cpu,
  Layers,
  Loader2,
  Lock,
  Mail,
  Scissors,
  ShieldCheck,
  TrendingUp,
  Eye,
  EyeOff,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login(email, password, UserRole.TENANT_ADMIN);
      // Success - redirect to inventory dashboard
      navigate('/inventory');
    } catch (err: unknown) {
      setError((err as Error).message || 'Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 font-sans">
      {/* ─── LEFT PANEL (60% Width) - About Product ────────────────── */}
      <div className="hidden lg:flex lg:w-3/5 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#022c22_1px,transparent_1px),linear-gradient(to_bottom,#022c22_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-35" />

        {/* Brand Header */}
        <div className="relative z-10 flex items-center space-x-3">
          <div className="w-10 h-10 rounded bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center backdrop-blur-md">
            <Scissors className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <span className="text-white font-bold tracking-tight text-lg block">TriaPOS</span>
            <span className="text-emerald-400/80 text-[10px] uppercase tracking-wider font-semibold">
              SaaS Enterprise Suite
            </span>
          </div>
        </div>

        {/* Product Value Propositions */}
        <div className="relative z-10 my-auto max-w-xl">
          <h1 className="text-4xl font-extrabold text-white tracking-tight leading-none mb-6">
            Fabricating the Future of <span className="text-emerald-400">TriaPOS</span>
          </h1>
          <p className="text-emerald-100/70 text-sm mb-10 leading-relaxed">
            The complete high-density, multi-tenant POS & inventory system designed specifically for
            textile manufacturers, apparel retailers, and fabric merchants.
          </p>

          <div className="space-y-4">
            {/* Feature Item 1 */}
            <div className="flex items-start space-x-4 p-4 rounded border border-emerald-500/10 bg-emerald-500/5 backdrop-blur-sm hover:bg-emerald-500/10 transition-all duration-300">
              <Layers className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-50">
                  Multi-Dimensional Grid Layouts
                </h4>
                <p className="text-xs text-emerald-100/60 mt-0.5">
                  Manage apparel variants across multiple colors, sizes, and raw materials with
                  ease.
                </p>
              </div>
            </div>

            {/* Feature Item 2 */}
            <div className="flex items-start space-x-4 p-4 rounded border border-emerald-500/10 bg-emerald-500/5 backdrop-blur-sm hover:bg-emerald-500/10 transition-all duration-300">
              <Cpu className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-50">
                  Lightning-Fast Billing Terminal
                </h4>
                <p className="text-xs text-emerald-100/60 mt-0.5">
                  Dual-tax GST calculation engine, instant receipt generation, and offline-resilient
                  cashier queues.
                </p>
              </div>
            </div>

            {/* Feature Item 3 */}
            <div className="flex items-start space-x-4 p-4 rounded border border-emerald-500/10 bg-emerald-500/5 backdrop-blur-sm hover:bg-emerald-500/10 transition-all duration-300">
              <TrendingUp className="w-5 h-5 text-emerald-400 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-50">
                  Analytics & Automatic GST Audit
                </h4>
                <p className="text-xs text-emerald-100/60 mt-0.5">
                  Generate compliance-ready CGST, SGST, and IGST breakdowns for direct financial
                  audits.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Metadata */}
        <div className="relative z-10 flex items-center justify-between text-emerald-300/40 text-[11px] border-t border-emerald-500/10 pt-6">
          <div className="flex items-center space-x-2">
            <Activity className="w-3.5 h-3.5 text-emerald-500 animate-pulse" />
            <span>Operational Cluster: AP-SOUTH-1</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            <span>SOC2 Type II Compliant</span>
          </div>
        </div>
      </div>

      {/* ─── RIGHT PANEL (40% Width) - Login Form ─────────────────── */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8 sm:p-12 md:p-16 bg-white border-l border-slate-200">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
              Sign in to Workspace
            </h2>
            <p className="text-xs text-slate-500">
              Enter your tenant identifier and credentials to access your terminal.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-600 font-medium animate-shake">
                {error}
              </div>
            )}

            {/* Email input */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-semibold text-slate-700">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded bg-slate-50 text-slate-950 placeholder-slate-400 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-colors duration-200"
                />
              </div>
            </div>

            {/* Password input */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label htmlFor="password" className="block text-xs font-semibold text-slate-700">
                  Password
                </label>
                <a
                  href="#forgot"
                  className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  Forgot?
                </a>
              </div>
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-9 pr-10 py-2 text-xs border border-slate-200 rounded bg-slate-50 text-slate-950 placeholder-slate-400 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-colors duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 text-slate-400 hover:text-slate-600 cursor-pointer"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500 border-slate-200 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-xs text-slate-500">
                Keep me signed in on this machine
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 border border-transparent rounded text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 flex items-center justify-center space-x-2 transition-all duration-200 hover:shadow-md cursor-pointer active:scale-[0.98]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verifying Terminal...</span>
                </>
              ) : (
                <span>Sign in to Terminal</span>
              )}
            </button>
          </form>

          {/* Form Footer */}
          <div className="text-center text-[10px] text-slate-400 pt-6">
            <span>By signing in, you agree to our </span>
            <a href="#terms" className="underline hover:text-slate-600">
              Terms of Service
            </a>
            <span> and </span>
            <a href="#privacy" className="underline hover:text-slate-600">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
